import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// The rail's per-app ⋯ menu (shared/_rail_app_menu). The app links are left
// exactly as nav-reorder and nav-more expect them, so instead of a button per
// row ONE ⋯ button floats over whichever app link is hovered or focused in
// the expanded rail, and ONE menu serves every app. Right-click on an app link
// opens the same menu in either rail state. Each action is a real form (the
// endpoints redirect back with a flash, as the /apps card menu's do); this
// controller only points the forms at the app and labels them.
export default class extends Controller {
    static targets = ["more", "menu", "about", "pinForm", "pinValue", "pinIcon", "pinLabel",
        "navForm", "muteForm", "muteValue", "muteIcon", "muteLabel", "uninstallForm", "newTab"]

    connect() {
        this._link = null
        this._open = null
        this._onOver = this._onOver.bind(this)
        this._onOut = this._onOut.bind(this)
        this._onContext = this._onContext.bind(this)
        this._onDocClick = this._onDocClick.bind(this)
        this._onKey = this._onKey.bind(this)
        this._hide = () => { this.closeMenu(); this._hideMore() }
        this.element.addEventListener("mouseover", this._onOver)
        this.element.addEventListener("mousemove", this._onOver)
        this.element.addEventListener("mouseout", this._onOut)
        this.element.addEventListener("focusin", this._onOver)
        this.element.addEventListener("contextmenu", this._onContext)
        this.element.addEventListener("scroll", this._hide, { passive: true })
        window.addEventListener("resize", this._hide)
        // A fresh page draws the rail collapsed and animates it open, so a link
        // hovered during that animation moves under the ⋯. Follow it.
        this._resize = new ResizeObserver(() => { if (this._link && !this._open && !this.moreTarget.hidden) this._place(this._link) })
        this._resize.observe(this.element)
    }

    disconnect() {
        this.element.removeEventListener("mouseover", this._onOver)
        this.element.removeEventListener("mousemove", this._onOver)
        this.element.removeEventListener("mouseout", this._onOut)
        this.element.removeEventListener("focusin", this._onOver)
        this.element.removeEventListener("contextmenu", this._onContext)
        this.element.removeEventListener("scroll", this._hide)
        window.removeEventListener("resize", this._hide)
        this._resize.disconnect()
        this.closeMenu()
        this._hideMore()
    }

    openFromButton(event) {
        event.preventDefault()
        event.stopPropagation()
        if (!this._link) return
        if (this._open === this._link) return this.closeMenu()
        const rect = this.moreTarget.getBoundingClientRect()
        this._openMenu(this._link, rect.left, rect.bottom + 4, rect.top)
    }

    leave(event) {
        if (this._open) return
        if (event.relatedTarget && this._link?.contains(event.relatedTarget)) return
        this._hideMore()
    }

    confirmUninstall(event) {
        const name = this._open?.dataset.appName || "this app"
        if (!window.confirm(`Uninstall ${name}? This deletes everything it stored for you - this can't be undone.`)) {
            event.preventDefault()
        }
    }

    closeMenu() {
        if (this.hasMenuTarget) {
            if (this.menuTarget.matches(":popover-open")) this.menuTarget.hidePopover()
            this.menuTarget.hidden = true
        }
        this._open?.classList.remove("is-menu-open")
        this._open = null
        document.removeEventListener("click", this._onDocClick)
        document.removeEventListener("keydown", this._onKey)
    }

    // The ⋯ only floats in the EXPANDED rail: over a 4rem icon rail it would
    // sit on top of the icon itself.
    _expanded() {
        return !!this.element.closest(".is-rail-expanded")
    }

    _appLink(target) {
        return target?.closest ? target.closest("a[data-rail-app]") : null
    }

    _onOver(event) {
        const link = this._appLink(event.target)
        if (!link || !this._expanded() || this._open) return
        if (!window.matchMedia("(min-width: 768px) and (hover: hover) and (pointer: fine)").matches) return
        this._link = link
        this._place(link)
    }

    _place(link) {
        const rect = link.getBoundingClientRect()
        const more = this.moreTarget
        more.hidden = false
        // The top layer uses viewport coordinates even inside a frosted,
        // scrolling rail. Older Turbo snapshots may lack the popover attribute.
        if (more.hasAttribute("popover") && !more.matches(":popover-open")) more.showPopover()
        more.setAttribute("aria-label", `Options for ${link.dataset.appName}`)
        more.style.top = `${rect.top + (rect.height - more.offsetHeight) / 2}px`
        more.style.left = `${rect.right - more.offsetWidth - 4}px`
    }

    _onOut(event) {
        if (this._open) return
        const link = this._appLink(event.target)
        if (!link) return
        const to = event.relatedTarget
        if (to && (link.contains(to) || this.moreTarget.contains(to))) return
        this._hideMore()
    }

    _onContext(event) {
        const link = this._appLink(event.target)
        if (!link) return
        event.preventDefault()
        this._link = link
        this._openMenu(link, event.clientX, event.clientY, event.clientY)
    }

    _hideMore() {
        if (this._open) return
        if (this.moreTarget.matches(":popover-open")) this.moreTarget.hidePopover()
        this.moreTarget.hidden = true
        this._link = null
    }

    _openMenu(link, left, top, flipTop) {
        this.closeMenu()
        this._fill(link)
        // Right-click opens this menu instead of the browser's, so it carries
        // the browser's most-missed item itself (core-nav-menu inherits it).
        if (this.hasNewTabTarget) this.newTabTarget.href = link.href
        this._open = link
        link.classList.add("is-menu-open")

        const menu = this.menuTarget
        menu.hidden = false
        menu.showPopover()
        const width = menu.offsetWidth
        const height = menu.offsetHeight
        menu.style.left = `${Math.max(8, Math.min(left, window.innerWidth - width - 8))}px`
        menu.style.top = `${top + height > window.innerHeight - 8 ? Math.max(8, flipTop - height - 4) : top}px`
        menu.querySelector("a, button")?.focus({ preventScroll: true })

        document.addEventListener("click", this._onDocClick)
        document.addEventListener("keydown", this._onKey)
    }

    _fill(link) {
        const data = link.dataset
        this.aboutTarget.href = data.appAboutUrl

        const configurable = !!data.appPreferenceUrl
        this.pinFormTarget.hidden = !configurable
        this.navFormTarget.hidden = !configurable
        if (configurable) {
            const pinned = data.appPinned === "true"
            this.pinFormTarget.action = data.appPreferenceUrl
            this.navFormTarget.action = data.appPreferenceUrl
            this.pinValueTarget.value = String(!pinned)
            this.pinLabelTarget.textContent = pinned ? "Unpin from home" : "Pin to home"
            this.pinIconTarget.className = `bi ${pinned ? "bi-pin-fill" : "bi-pin"}`
        }

        const canMute = data.appNotifications !== undefined
        this.muteFormTarget.hidden = !canMute
        if (canMute) {
            const on = data.appNotifications === "true"
            this.muteFormTarget.action = data.appPreferenceUrl
            this.muteValueTarget.value = String(!on)
            this.muteLabelTarget.textContent = on ? "Mute notifications" : "Unmute notifications"
            this.muteIconTarget.className = `bi ${on ? "bi-bell-slash" : "bi-bell"}`
        }

        this.uninstallFormTarget.hidden = !data.appUninstallUrl
        if (data.appUninstallUrl) this.uninstallFormTarget.action = data.appUninstallUrl

        // The forms render with a placeholder action, and per-form CSRF tokens
        // are bound to the action they were rendered for - so use the page's
        // own token, which is valid for any action.
        const token = csrfToken()
        this.menuTarget.querySelectorAll("input[name='authenticity_token']").forEach((input) => { input.value = token })
    }

    _onDocClick(event) {
        if (this.menuTarget.contains(event.target) || this.moreTarget.contains(event.target)) return
        this.closeMenu()
        this._hideMore()
    }

    _onKey(event) {
        if (event.key !== "Escape") return
        const link = this._open
        this.closeMenu()
        this._hideMore()
        link?.focus()
    }
}
